import React, { useState } from "react";
import { Download, Database, Lock, Edit3 } from "lucide-react";
import { Field, TextInput } from "@/components/ReportPrimitives";
import { UnitToggle, TextField, NumField, NotesField, u } from "./_shared";
import PullFromProfilesDrawer from "@/components/PullFromProfilesDrawer";
import { useEngineeringSession } from "@/lib/rce/useEngineeringSession";
import { sessionToResolverCtx } from "@/lib/rce/useReportRceWiring";
import { deriveMudGradient } from "@/lib/calc/mudAutoCalc";

// OperationalStateProfileForm — dynamic operational data.
// Separated from Well Profile (req #4: static vs dynamic split).
//
// "Apply Available Values" fills fields strictly from the CANONICAL records
// bound to the Engineering Session, via the RCE resolver: the Rig Data Sheet
// (rig.name, rig.pumpOutputBblStk), the Well Profile (current MD/TVD, hole
// size — which prefers a bound BHA's bit size — and hole section from the
// casing program), and mud weight from the latest Daily Mud Report falling
// back to the Mud Program. Audit trace is stored on the payload as
// `_importedFrom` so downstream consumers can surface provenance.
//
// Operational State Canonical Pull Values Correction (P0) — the drawer is
// opened with `canonicalOnly`, so there is no legacy Profile Manager picker
// and no legacy sourceKind/sourceField fallback for these fields; every
// canonical row resolves strictly through the RCE resolver via `rcePath`.
// Mud gradient, active surface volume, pump rate (SPM) and slow-pump
// pressure have NO canonical source anywhere in RigSentinel today (they are
// live operational readings, not stored geometry/spec) — `manual: true`
// renders them in the drawer's separate "Manual / Live Values" list, never
// touched by Apply, until a Slow-Pump Survey module exists.
//
// Mud gradient's `manual: true` above is about the Engineering Setup PULL
// drawer only (there is still no canonical/RCE source for it) -- it is
// UNRELATED to the fact that gradient is a deterministic function of the
// mud weight already typed on THIS SAME form. That derivation is handled
// below (deriveMudGradient + an Auto/Override toggle, same pattern as
// MudProfileForm.jsx / mudAutoCalc.js), independently of the pull drawer.
// Do NOT remove `manual: true` here -- doing so would move this row into
// the drawer's "Available" table with no rcePath/sourceKind, which would
// reintroduce the exact "misleading disabled import checkbox" bug the P0
// correction above was written to fix.

const OPS_PULL_MAPPINGS = [
  // From the active Rig Data Sheet — canonical-only. There is no legacy "rig"
  // profile kind, so this mapping carries no sourceKind and resolves through
  // the RCE resolver alone.
  { section: "Rig", field: "rig", label: "Rig", rcePath: "rig.name" },

  // From Well Profile — canonical via RCE resolver, strictly.
  { section: "Depths & hole geometry", field: "currentMd",   label: "Current MD",  unit: "ft / m",
    rcePath: "well.currentMdFt" },
  { section: "Depths & hole geometry", field: "currentTvd",  label: "Current TVD", unit: "ft / m",
    rcePath: "well.currentTvdFt",
    // Shown by the drawer when the canonical resolver returns this value at
    // "derived" confidence, which is how derivers.js publishes MD-as-TVD.
    // Display only — the deriver, its arithmetic and its confidence are
    // untouched, and no confirmation is required to import.
    approxNote: "TVD unavailable — MD is being used as an approximation. Verify TVD before engineering use." },
  { section: "Depths & hole geometry", field: "holeSize",    label: "Hole size",   unit: "in / mm",
    rcePath: "well.holeSizeIn" },
  // Canonical-only (no sourceKind): the legacy well profile has no holeSection
  // field, so there is no legacy fallback to point at. The deriver already
  // exists — this mapping was simply never added.
  { section: "Depths & hole geometry", field: "holeSection", label: "Hole section",
    rcePath: "well.holeSection" },

  // From Mud Profile — mud weight is canonical via the active Mud Program.
  { section: "Mud", field: "currentMudDensity",  label: "Current mud weight",  unit: "ppg / kg·m⁻³",
    rcePath: "mud.weightInPpg" },
  // Operational State Pull correction — mud gradient, active surface volume,
  // pump rate (SPM) and slow-pump pressure have NO canonical source anywhere
  // in RigSentinel today. `manual: true` renders them in the drawer's
  // separate Manual / Live Values list instead of the Available table, with
  // no (misleading) disabled import checkbox.
  { section: "Mud", field: "currentMudGradient", label: "Current mud gradient", unit: "psi/ft / kPa·m⁻¹",
    manual: true, manualNote: "Enter manually" },
  { section: "Mud", field: "activeSurfaceVolume", label: "Active surface volume", unit: "bbl / m³",
    manual: true, manualNote: "Enter manually" },

  // Pump output is canonical via the active Rig Data Sheet's default
  // circulating pump; SPM and slow-pump pressure are live readings with no
  // stored source (see `manual: true` note above).
  { section: "Pump", field: "currentPumpSpm",            label: "Current pump rate (SPM)", unit: "spm",
    manual: true, manualNote: "Live reading / enter manually" },
  { section: "Pump", field: "currentSlowPumpPressure",   label: "Slow pump pressure",      unit: "psi / kPa",
    manual: true, manualNote: "Live reading / enter manually" },
  { section: "Pump", field: "currentPumpDisplacement",   label: "Pump output / displacement", unit: "bbl/stk / m³/stk",
    rcePath: "rig.pumpOutputBblStk" },
];

export default function OperationalStateProfileForm({ value, onChange }) {
  const units = value.units || "IMP";
  const set = (patch) => onChange({ ...value, ...patch });
  const [pullOpen, setPullOpen] = useState(false);

  // Mud gradient auto-derives from Current MW, same canonical
  // deriveMudGradient() + Auto/Override pattern as MudProfileForm.jsx.
  // Defaults to auto when unset so existing saved OpStates are unaffected.
  const gradAuto = value._gradientManual !== true;
  const derivedGrad = deriveMudGradient(value.currentMudDensity, units);
  const roundGrad = (v) => (v == null ? "" : String(Math.round(v * 10000) / 10000));
  const gradDisplay = gradAuto && derivedGrad != null ? roundGrad(derivedGrad) : (value.currentMudGradient ?? "");
  const setMudDensity = (v) => {
    const patch = { currentMudDensity: v };
    if (gradAuto) patch.currentMudGradient = roundGrad(deriveMudGradient(v, units));
    set(patch);
  };
  const setUnitsWithGrad = (newUnits) => {
    const patch = { units: newUnits };
    if (gradAuto) patch.currentMudGradient = roundGrad(deriveMudGradient(value.currentMudDensity, newUnits));
    set(patch);
  };
  const toggleGradAuto = () => {
    if (!gradAuto) {
      set({ _gradientManual: false, currentMudGradient: roundGrad(deriveMudGradient(value.currentMudDensity, units)) });
    } else {
      set({ _gradientManual: true });
    }
  };
  const { session } = useEngineeringSession();
  // Hole Section (this drawer only): prefer the active Engineering Session's
  // own Hole Section over the casing-derived value, matching how the rest of
  // the app treats an explicit session choice. Passed via sessionToResolverCtx's
  // existing `extra` param, so only this form's rceCtx carries it - no other
  // caller of sessionToResolverCtx or the shared resolver is affected.
  const rceCtx = sessionToResolverCtx(session, { holeSection: session?.holeSection || "" });

  const handleApply = (patch, importedFrom) => {
    const next = { ...value, ...patch };
    next._importedFrom = { ...(value._importedFrom || {}), ...importedFrom };
    // "Pull values from Engineering Setup" can bring in a new mud weight
    // (mud.weightInPpg has a canonical source; gradient does not). Keep the
    // same auto-derive behavior as typing it directly, so pulling mud
    // weight also fills gradient rather than leaving it stale/blank.
    if (gradAuto && Object.prototype.hasOwnProperty.call(patch, "currentMudDensity")) {
      next.currentMudGradient = roundGrad(deriveMudGradient(next.currentMudDensity, next.units || units));
    }
    onChange(next);
    setPullOpen(false);
  };

  const importedKeys = Object.keys(value._importedFrom || {});

  return (
    <div className="flex flex-col gap-4" data-testid="opstate-profile-form">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10.5px] text-[var(--rs-text-3)] leading-snug">
          Tip: tap <strong className="text-[var(--rs-amber)]">Pull values from Engineering Setup</strong> to
          fill fields from the records bound to this session — depths and hole
          data from the Well Profile, mud weight from the latest Daily Mud
          Report or the Mud Program, and rig name and pump output from the Rig
          Data Sheet. Mud gradient, active surface volume, pump rate and slow
          pump pressure have no stored source and stay manual/live entries;
          every imported field is logged.
        </div>
        <button type="button"
                data-testid="opstate-pull-btn"
                onClick={() => setPullOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium whitespace-nowrap"
                style={{
                  background: "var(--rs-amber)",
                  color: "#0A0B0E",
                  minHeight: 36,
                }}>
          <Download className="w-4 h-4" /> Pull values from Engineering Setup
        </button>
      </div>

      {importedKeys.length > 0 && (
        <div data-testid="opstate-import-audit"
             className="card-rs p-2 text-[10.5px]"
             style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.05)" }}>
          <div className="font-mono uppercase tracking-[0.2em] text-[var(--rs-amber)] mb-1 inline-flex items-center gap-1">
            <Database className="w-3 h-3" /> Imported {importedKeys.length} value{importedKeys.length === 1 ? "" : "s"} from profiles
          </div>
          <div className="text-[var(--rs-text-2)] leading-snug">
            {importedKeys.map((k) => {
              const src = value._importedFrom[k];
              return src.kind === "rce" ? `${k} ← ${src.field}` : `${k} ← ${src.kind}#${src.id}@v${src.version}`;
            }).join(" · ")}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TextField value={value} onChange={onChange} field="label" label="Shift label"
                   placeholder="e.g. Day Tour 14-Feb 06:00" testId="opstate-label" />
        <TextField value={value} onChange={onChange} field="rig" label="Rig (optional)"
                   placeholder="e.g. Rig Alpha" testId="opstate-rig" />
        <UnitToggle value={units} onChange={setUnitsWithGrad} testId="opstate-units" />
      </div>
      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Current operations
        </legend>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <NumField value={value} onChange={onChange} field="currentMd"          label="Current MD"             units={units} kind="depth"    testId="opstate-md" />
          <NumField value={value} onChange={onChange} field="currentTvd"         label="Current TVD"            units={units} kind="depth"    testId="opstate-tvd" />
          <NumField value={value} onChange={onChange} field="holeSize"           label="Hole size (bit OD)"     units={units} kind="dia"      testId="opstate-holeSize" />
          <Field label={`Current MW (${u.density(units)})`}>
            <TextInput type="number" value={value.currentMudDensity ?? ""} onChange={setMudDensity} testId="opstate-mw" />
          </Field>
          <Field label={`Current Gradient (${u.gradient(units)})`}
                 helper={gradAuto ? "Auto — derived from Current MW." : "Manual override. Click Auto to recompute."}>
            <div className="flex items-stretch gap-2">
              <div className="flex-1">
                <TextInput type="number" value={gradDisplay} onChange={(v) => set({ currentMudGradient: v })}
                           readOnly={gradAuto} testId="opstate-grad" />
              </div>
              <button type="button" onClick={toggleGradAuto}
                      data-testid="opstate-grad-toggle"
                      title={gradAuto ? "Switch to manual override" : "Reset to auto-derive"}
                      className="text-[10px] px-2 rounded-md whitespace-nowrap inline-flex items-center gap-1"
                      style={{
                        minHeight: 36,
                        background: gradAuto ? "rgba(245,180,0,0.10)" : "rgba(22,24,29,0.6)",
                        color: gradAuto ? "var(--rs-amber)" : "var(--rs-text-2)",
                        border: `1px solid ${gradAuto ? "rgba(245,180,0,0.35)" : "var(--rs-border)"}`,
                      }}>
                {gradAuto ? (<><Lock className="w-3 h-3" /> Auto</>) : (<><Edit3 className="w-3 h-3" /> Override</>)}
              </button>
            </div>
          </Field>
          <NumField value={value} onChange={onChange} field="activeSurfaceVolume" label="Active surface vol"    units={units} kind="volume"   testId="opstate-surf" />
        </div>
        <TextField value={value} onChange={onChange} field="holeSection" label="Hole section label"
                   placeholder='e.g. "12¼ in intermediate"' testId="opstate-section" />
      </fieldset>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Current pump operations
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <NumField value={value} onChange={onChange} field="currentPumpSpm"
                    label="Pump rate (SPM)" units={units} kind="spm" testId="opstate-pumpSpm"
                    helper="Enter manually — a live reading with no stored source to pull from." />
          <NumField value={value} onChange={onChange} field="currentSlowPumpPressure"
                    label="Slow pump pressure" units={units} kind="press" testId="opstate-slowPumpPress"
                    helper="Enter manually — a live reading with no stored source to pull from." />
          <NumField value={value} onChange={onChange} field="currentPumpDisplacement"
                    label="Pump output / displacement" units={units} kind="disp" testId="opstate-pumpDisp"
                    helper="Filled by Pull values from the rig's default pump on the Rig Data Sheet." />
        </div>
      </fieldset>

      <NotesField value={value} onChange={onChange} testId="opstate-notes" />

      <PullFromProfilesDrawer
        open={pullOpen}
        title="Apply Available Values"
        target={value}
        mappings={OPS_PULL_MAPPINGS}
        onApply={handleApply}
        onClose={() => setPullOpen(false)}
        rceCtx={rceCtx}
        canonicalOnly
        applyButtonLabel="Apply Available Values"
      />
    </div>
  );
}
